import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@axios'

export const useFansStore = defineStore('fans', () => {
  const fans = ref([])
  const total = ref(0)
  const loading = ref(false)
  const error = ref(null)

  // Restaurants making up the group-widened enrichment scope (id/name/market),
  // returned by the list endpoint. Used to resolve names for the "Fan of" line,
  // which can include sibling restaurants the admin doesn't directly manage.
  const enrichmentRestaurants = ref([])

  // preferredOnly / highlightsOnly are the Preferred Members and Member
  // Highlights chips: the same list, filtered (Kapil, 2026-09-25).
  const filters = ref({
    restaurantId: null,
    preferredOnly: false,
    highlightsOnly: false,
  })
  const search = ref('')
  const sort = ref('tier')
  const limit = ref(30)
  const skip = ref(0)

  const hasMore = computed(() => fans.value.length < total.value)

  // ─── Multi-select state ─────────────────────────────────────────────────────
  // Keyed by fan _id → fan object. Storing the object (not just the id) lets the
  // selection include fans picked via "Select all" that are not in the loaded
  // page — the bulk-message composer needs each selected fan's full record.
  const selectedFansMap = ref(new Map())
  const loadingAllFans = ref(false)
  const allFansError = ref(null)

  const selectedCount = computed(() => selectedFansMap.value.size)

  const selectedFans = computed(() => [...selectedFansMap.value.values()])

  function toggleSelect(fan) {
    if (!fan?._id) return
    const next = new Map(selectedFansMap.value)
    if (next.has(fan._id)) {
      next.delete(fan._id)
    } else {
      next.set(fan._id, fan)
    }
    selectedFansMap.value = next
  }

  function clearSelection() {
    selectedFansMap.value = new Map()
    allFansError.value = null
  }

  function isSelected(fanId) {
    return selectedFansMap.value.has(fanId)
  }

  /**
   * Fetch a lightweight list of EVERY fan matching the current filters/search
   * — not just the loaded page. Returns the array; does NOT mutate the
   * individual-checkbox selection. Backs the "Message All" action.
   */
  async function fetchAllMatchingFans() {
    loadingAllFans.value = true
    allFansError.value = null
    try {
      const params = {}
      if (filters.value.restaurantId) params.restaurantId = filters.value.restaurantId
      if (filters.value.preferredOnly) params.preferredOnly = 'true'
      if (filters.value.highlightsOnly) params.highlightsOnly = 'true'
      if (search.value?.trim()) params.search = search.value.trim()

      const { data } = await axios.get('/restaurant-admin/fans/all', { params })

      return data.fans || []
    } catch (err) {
      allFansError.value = err?.response?.data?.error || 'Failed to load fans'

      return []
    } finally {
      loadingAllFans.value = false
    }
  }

  function setFilters(newFilters) {
    filters.value = { ...filters.value, ...newFilters }
    skip.value = 0
    fans.value = []
    total.value = 0
  }

  function setSearch(value) {
    search.value = value
    skip.value = 0
    fans.value = []
    total.value = 0
  }

  function setSort(value) {
    sort.value = value
    skip.value = 0
    fans.value = []
    total.value = 0
  }

  async function fetchFans(reset = false) {
    if (reset) {
      skip.value = 0
      fans.value = []
      total.value = 0
    }
    if (loading.value) return

    loading.value = true
    error.value = null

    try {
      const params = {
        limit: limit.value,
        skip: skip.value,
        sort: sort.value,
      }
      if (filters.value.restaurantId) params.restaurantId = filters.value.restaurantId
      if (filters.value.preferredOnly) params.preferredOnly = 'true'
      if (filters.value.highlightsOnly) params.highlightsOnly = 'true'
      if (search.value?.trim()) params.search = search.value.trim()

      const { data } = await axios.get('/restaurant-admin/fans', { params })

      const newFans = data.fans || []
      if (reset || skip.value === 0) {
        fans.value = newFans
      } else {
        fans.value = [...fans.value, ...newFans]
      }
      if (Array.isArray(data.enrichmentRestaurants)) {
        enrichmentRestaurants.value = data.enrichmentRestaurants
      }
      total.value = data.total || 0
      skip.value = fans.value.length
    } catch (err) {
      error.value = err?.response?.data?.error || 'Failed to load fans'
    } finally {
      loading.value = false
    }
  }

  async function loadMore() {
    if (!hasMore.value || loading.value) return
    await fetchFans(false)
  }

  function reset() {
    fans.value = []
    total.value = 0
    loading.value = false
    error.value = null
    filters.value = { restaurantId: null, preferredOnly: false, highlightsOnly: false }
    search.value = ''
    sort.value = 'tier'
    skip.value = 0
    clearSelection()
  }

  function updateFan(fanId, patch) {
    const idx = fans.value.findIndex(f => f._id === fanId || f._id?.toString?.() === fanId?.toString?.())
    if (idx === -1) return
    fans.value = [
      ...fans.value.slice(0, idx),
      { ...fans.value[idx], ...patch },
      ...fans.value.slice(idx + 1),
    ]
  }

  return {
    fans,
    total,
    loading,
    error,
    enrichmentRestaurants,
    filters,
    search,
    sort,
    limit,
    skip,
    hasMore,
    selectedCount,
    selectedFans,
    loadingAllFans,
    allFansError,
    setFilters,
    setSearch,
    setSort,
    fetchFans,
    loadMore,
    reset,
    toggleSelect,
    fetchAllMatchingFans,
    clearSelection,
    isSelected,
    updateFan,
  }
})
